import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, ArrowRight, Search, ChevronDown } from "lucide-react";
import { track } from "../lib/api";
import SearchDialog from "./SearchDialog";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
} from "./ui/dropdown-menu";
import { LANDC_PARTNER_URL, BROKER_LINK_REL } from "../lib/broker";
import { cashbackPromotionEnabled } from "../config/promotions";

/* --- Nav configuration ---
   Groups: Tools ▾ | Guides ▾ | Next steps ▾
   The broker item under Next steps links directly to our live L&C partner. */
const TOOLS = [
  { to: "/snapshot", label: "Mortgage Snapshot", desc: "Your key mortgage figures in 2 minutes" },
  { to: "/calculator", label: "Mortgage Calculator", desc: "Repayments, LTV and overpayments" },
  { to: "/stamp-duty-calculator", label: "Stamp Duty Calculator", desc: "SDLT, LTT and LBTT across the UK" },
];
const GUIDES = [
  { to: "/first-time-buyer-guide", label: "First-Time Buyer Guide", desc: "The full UK buying journey, plain English" },
  { to: "/remortgage-guide", label: "Remortgage Guide", desc: "Timing, product transfer vs remortgage" },
];
const NEXT_STEPS = [
  { href: LANDC_PARTNER_URL, external: true, broker: true, label: "Get mortgage help", desc: "Expert mortgage advice that won't cost you a penny" },
  { to: "/conveyancing-quote?source=nav_next_steps_conveyancing", label: "Get a conveyancing quote", desc: "A clear quote from our UK conveyancing partner", event: "next_steps_conveyancing_clicked", location: "nav_next_steps_conveyancing" },
  { to: "/survey-quote?source=nav_next_steps_survey", label: "Book a property survey", desc: "RICS-regulated home surveys from our surveying partner", event: "next_steps_survey_clicked", location: "nav_next_steps_survey" },
  { to: "/cashback", label: "£100 Cashback", desc: "Claim £100 when your purchase completes", event: "next_steps_cashback_clicked", location: "nav_next_steps_cashback" },
];

/* While the cashback promotion is disabled for compliance review, drop the
   £100 Cashback item from the Next steps menu (desktop + mobile). Reversible. */
const NEXT_STEPS_VISIBLE = NEXT_STEPS.filter((it) => it.to !== "/cashback" || cashbackPromotionEnabled);

const DesktopMenu = ({ label, items, testid, onOpenTrack, brokerSource }) => {
  const [open, setOpen] = useState(false);
  const onChange = (v) => {
    setOpen(v);
    if (v && onOpenTrack) onOpenTrack();
  };
  return (
    <DropdownMenu open={open} onOpenChange={onChange}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          data-testid={`nav-${testid}`}
          className={`inline-flex items-center gap-1 text-[13px] font-medium tracking-tight transition-colors whitespace-nowrap outline-none ${open ? "text-[#0B5F58]" : "text-[#475569] hover:text-[#0F766E]"}`}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          {label}
          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        sideOffset={10}
        className="min-w-[280px] rounded-2xl border border-[#DCE7E3] bg-white p-2 shadow-xl"
        data-testid={`nav-${testid}-menu`}
      >
        <DropdownMenuLabel className="px-3 pt-1.5 pb-1 text-[10px] uppercase tracking-widest text-[#0F766E]">
          {label}
        </DropdownMenuLabel>
        {items.map((it) => {
          const testId = `nav-${testid}-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
          const itemClass = "flex flex-col gap-0.5 rounded-xl px-3 py-2.5 cursor-pointer outline-none focus:bg-[#F7FAF9] hover:bg-[#F7FAF9] transition-colors";
          const onItemClick = () => {
            if (it.broker) track("broker_click", { source: brokerSource, cta_location: "nav_next_steps_broker", partner: "L&C", destination: it.href, destination_type: "external" });
            else if (it.event) track(it.event, { cta_location: it.location || testid });
          };
          const inner = (
            <>
              <span className="text-sm font-semibold text-[#0B1220]">{it.label}</span>
              {it.desc && <span className="text-xs text-[#475569] leading-snug">{it.desc}</span>}
            </>
          );
          return (
            <DropdownMenuItem key={it.to || it.href} asChild>
              {it.external ? (
                <a href={it.href} target="_blank" rel={BROKER_LINK_REL} onClick={onItemClick} data-testid={testId} className={itemClass}>
                  {inner}
                </a>
              ) : (
                <Link to={it.to} onClick={onItemClick} data-testid={testId} className={itemClass}>
                  {inner}
                </Link>
              )}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const NavA = ({ to, children, testid }) => (
  <NavLink
    to={to}
    end={to === "/"}
    data-testid={`nav-${testid}`}
    className={({ isActive }) =>
      `text-[13px] font-medium tracking-tight transition-colors whitespace-nowrap ${isActive ? "text-[#0B5F58]" : "text-[#475569] hover:text-[#0F766E]"}`
    }
  >
    {children}
  </NavLink>
);

/* ---------- Mobile grouped nav (controlled accordion — only one open at a time) ---------- */
const MobileGroup = ({ label, items, testid, onClickItem, onOpenTrack, brokerSource, isOpen, onToggle }) => (
  <details className="group border-b border-[#DCE7E3] last:border-b-0 py-1" data-testid={`mnav-${testid}`} open={isOpen}>
    <summary
      className="flex items-center justify-between py-2.5 text-base font-medium text-[#0B1220] cursor-pointer list-none marker:hidden"
      onClick={(e) => { e.preventDefault(); if (!isOpen && onOpenTrack) onOpenTrack(); onToggle?.(); }}
    >
      {label}
      <ChevronDown className="w-4 h-4 text-[#0F766E] transition-transform group-open:rotate-180" aria-hidden="true" />
    </summary>
    <div className="pl-1 pb-2 grid gap-1">
      {items.map((it) => {
        const testId = `mnav-${testid}-${it.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
        const itemClass = "flex flex-col rounded-lg px-3 py-2 text-[15px] text-[#0B1220] hover:bg-[#F7FAF9]";
        const onItemClick = () => {
          onClickItem?.();
          if (it.broker) track("broker_click", { source: brokerSource, cta_location: "nav_next_steps_broker", partner: "L&C", destination: it.href, destination_type: "external" });
          else if (it.event) track(it.event, { cta_location: it.location || testid });
        };
        const inner = (
          <>
            <span className="font-medium">{it.label}</span>
            {it.desc && <span className="text-xs text-[#475569]">{it.desc}</span>}
          </>
        );
        return it.external ? (
          <a key={it.href} href={it.href} target="_blank" rel={BROKER_LINK_REL} onClick={onItemClick} data-testid={testId} className={itemClass}>
            {inner}
          </a>
        ) : (
          <Link key={it.to} to={it.to} onClick={onItemClick} data-testid={testId} className={itemClass}>
            {inner}
          </Link>
        );
      })}
    </div>
  </details>
);

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileGroup, setMobileGroup] = useState(null);
  const openSearch = (source) => { setSearchOpen(true); track("search_opened", { source }); };

  // Reset the mobile accordion whenever the hamburger menu closes so it opens cleanly.
  useEffect(() => { if (!open) setMobileGroup(null); }, [open]);
  const toggleMobileGroup = (id) => setMobileGroup((g) => (g === id ? null : id));

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchOpen(true);
        track("search_opened", { source: "keyboard" });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onNextStepsOpen = () => track("next_steps_opened", { cta_location: "nav_next_steps", source: "nav_next_steps" });

  return (
    <header className="sticky top-0 z-40 backdrop-blur bg-[#F7FAF9]/90 border-b border-[#DCE7E3]">
      <div className="mp-container flex items-center gap-6 justify-between py-3.5">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-logo">
          <span className="font-sora font-bold tracking-tight text-[#0F766E] text-2xl">Mortgage<span className="text-[#10B981]">Path</span></span>
        </Link>
        <nav className="hidden lg:flex items-center gap-5 xl:gap-6" aria-label="Primary">
          <NavA to="/" testid="home">Home</NavA>
          <DesktopMenu label="Tools" items={TOOLS} testid="tools" />
          <DesktopMenu label="Guides" items={GUIDES} testid="guides" />
          <DesktopMenu label="Next steps" items={NEXT_STEPS_VISIBLE} testid="next-steps" onOpenTrack={onNextStepsOpen} brokerSource="tools" />
          <NavA to="/rewards" testid="rewards">Rewards</NavA>
          <NavA to="/faqs" testid="faqs">FAQs</NavA>
          <NavA to="/contact" testid="contact">Contact</NavA>
        </nav>
        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={() => openSearch("header")}
            data-testid="header-search-button"
            aria-label="Search guides and FAQs"
            className="flex items-center gap-2 text-[13px] font-medium text-[#475569] hover:text-[#0F766E] border border-[#DCE7E3] hover:border-[#0F766E]/40 rounded-full px-3.5 py-2 transition-colors"
          >
            <Search className="w-3.5 h-3.5" /> <span className="hidden xl:inline">Search</span>
            <kbd className="hidden xl:inline text-[10px] text-[#94A3B8] border border-[#DCE7E3] rounded px-1 py-px">Ctrl K</kbd>
          </button>
          <Link to="/snapshot" className="mp-btn-primary text-sm whitespace-nowrap" data-testid="header-cta-snapshot" onClick={() => track("snapshot_start_clicked", { cta_location: "header_nav", cta_text: "Start free check" })}>
            Start free check <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="flex items-center gap-2 lg:hidden">
          <button
            onClick={() => openSearch("header_mobile")}
            data-testid="mobile-search-button"
            aria-label="Search guides and FAQs"
            className="p-2 rounded-lg border border-[#DCE7E3] text-[#0F766E] hover:bg-[#0F766E]/5 transition-colors"
          >
            <Search className="w-5 h-5" />
          </button>
          <button
            className="p-2 rounded-lg border border-[#DCE7E3] text-[#0F766E] hover:bg-[#0F766E]/5 transition-colors"
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            aria-expanded={open}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && (
        <>
          <div className="lg:hidden fixed inset-0 top-[100px] bg-black/30 backdrop-blur-sm z-30" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="lg:hidden border-t border-[#DCE7E3] bg-white relative z-40 shadow-lg">
            <div className="mp-container py-4 flex flex-col gap-1">
              <NavLink
                to="/"
                end
                onClick={() => setOpen(false)}
                className={({ isActive }) => `py-2.5 text-base font-medium border-b border-[#DCE7E3] ${isActive ? "text-[#0B5F58]" : "text-[#0B1220] hover:text-[#0F766E]"}`}
              >
                Home
              </NavLink>
              <MobileGroup label="Tools" items={TOOLS} testid="tools" onClickItem={() => setOpen(false)} isOpen={mobileGroup === "tools"} onToggle={() => toggleMobileGroup("tools")} />
              <MobileGroup label="Guides" items={GUIDES} testid="guides" onClickItem={() => setOpen(false)} isOpen={mobileGroup === "guides"} onToggle={() => toggleMobileGroup("guides")} />
              <MobileGroup label="Next steps" items={NEXT_STEPS_VISIBLE} testid="next-steps" onClickItem={() => setOpen(false)} onOpenTrack={onNextStepsOpen} brokerSource="tools" isOpen={mobileGroup === "next-steps"} onToggle={() => toggleMobileGroup("next-steps")} />
              <NavLink
                to="/rewards"
                onClick={() => setOpen(false)}
                className={({ isActive }) => `py-2.5 text-base font-medium border-b border-[#DCE7E3] ${isActive ? "text-[#0B5F58]" : "text-[#0B1220] hover:text-[#0F766E]"}`}
              >
                Rewards
              </NavLink>
              <NavLink
                to="/faqs"
                onClick={() => setOpen(false)}
                className={({ isActive }) => `py-2.5 text-base font-medium border-b border-[#DCE7E3] last:border-b-0 ${isActive ? "text-[#0B5F58]" : "text-[#0B1220] hover:text-[#0F766E]"}`}
              >
                FAQs
              </NavLink>
              <NavLink
                to="/contact"
                onClick={() => setOpen(false)}
                className={({ isActive }) => `py-2.5 text-base font-medium ${isActive ? "text-[#0B5F58]" : "text-[#0B1220] hover:text-[#0F766E]"}`}
              >
                Contact
              </NavLink>
              <Link to="/snapshot" onClick={() => { setOpen(false); track("snapshot_start_clicked", { cta_location: "mobile_nav", cta_text: "Start free check" }); }} className="mp-btn-primary justify-center mt-3" data-testid="mobile-cta-snapshot">
                Start free mortgage check <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </>
      )}
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
};

export default Header;
